import { Col, Row } from "antd";
import { ColumnProps } from "antd/lib/table/Column";
import { TFunction } from "i18next";
import { AppUser, AppUserFilter } from "models/AppUser";
import { AppUserGrouping } from "models/AppUserGrouping";
import { Organization } from "models/Organization";
import React, { Dispatch, Key, SetStateAction, useMemo } from "react";
import {
  AdvanceStringFilter,
  LayoutCell,
  LayoutHeader,
  Modal,
  OneLineText,
  Pagination,
  StandardTable,
} from "react3l-ui-library";
import { appUserGroupingRepository } from "repositories/app-user-grouping-repository";
import { finalize, forkJoin } from "rxjs";
import { webService } from "services/common-services/web-service";
import nameof from "ts-nameof.macro";
import { v4 as uuidv4 } from "uuid";
import { Close20 } from "@carbon/icons-react";
import { authorizationService } from "services/common-services/authorization-service";
import { APP_USER_GROUPING_ROUTE } from "config/route-consts";
import { API_APP_USER_GROUPING_PREFIX } from "config/api-consts";
import { RowSelectionType } from "antd/lib/table/interface";
import _cloneDeep from "lodash/cloneDeep";
import { debounce } from "lodash";

export function useAppUserGroupingAppUserHook(
  model: AppUserGrouping,
  setModel: Dispatch<SetStateAction<AppUserGrouping>>,
  handleLoadList: (filter: AppUserFilter) => void,
  filterAppUserTable: AppUserFilter,
  listAppUser?: AppUser
) {
  const [visible, setVisible] = React.useState<boolean>(false);
  const [subscription] = webService.useSubscription();
  const [appUserFilter, setAppUserFilter] = React.useState<AppUserFilter>(
    new AppUserFilter()
  );
  const [list, setList] = React.useState<AppUser[]>([]);
  const [total, setTotal] = React.useState<number>(undefined);
  const [loading, setLoading] = React.useState<boolean>();
  // const [selectedRowKeys, setSelectedRowKeys] = React.useState<KeyType[]>([]);
  const [selectedList, setSelectedList] = React.useState<AppUser[]>([]);

  const typeRowSelection: RowSelectionType = "checkbox";
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<Key[]>([]);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [isSave, setIsSave] = React.useState(false);

  // const rowSelection = useMemo(
  //   () => ({
  //     onChange(selectedRowKeys: KeyType[], selectedRows: AppUser[]) {
  //       setSelectedList([...selectedRows]);
  //       setSelectedRowKeys(selectedRowKeys);
  //     },
  //     selectedRowKeys,
  //     type: "checkbox",
  //   }),
  //   [selectedRowKeys]
  // );

  const pushArrSelected = React.useCallback(
    (item: any) => {
      const selectedRowKeysHere = _cloneDeep(selectedRowKeys);
      const selectedRowsHere = _cloneDeep(selectedRows);
      selectedRowKeysHere.push(item?.id);
      selectedRowsHere.push(item);
      setSelectedRowKeys(selectedRowKeysHere);
      setSelectedRows(selectedRowsHere);
      setSelectedList(selectedRowsHere);
    },
    [selectedRowKeys, selectedRows]
  );

  const deleteArrNotSelect = React.useCallback(
    (item: AppUserGrouping) => {
      const selectedRowKeysHere = _cloneDeep(selectedRowKeys);
      const selectedRowsHere = _cloneDeep(selectedRows);
      selectedRowKeysHere.splice(selectedRowKeysHere?.indexOf(item?.id), 1);
      const index = selectedRowsHere?.findIndex((obj: any) => {
        return obj?.id === item?.id;
      });
      selectedRowsHere.splice(index, 1);
      setSelectedRowKeys(selectedRowKeysHere);
      setSelectedRows(selectedRowsHere);
    },
    [selectedRowKeys, selectedRows]
  );

  const handleCheckAll = React.useCallback(
    (selected: boolean, rows: any[], changeRows: any[]) => {
      const selectedIds = changeRows.map((row: any) => row.id);
      const selectedRowKeysHere = _cloneDeep(selectedRowKeys);
      const selectedRowsHere = _cloneDeep(selectedRows);
      if (selected) {
        const selectedRowKeysHereSet = selectedRowKeysHere.concat(selectedIds);
        const selectedRowsHereSet = selectedRowsHere.concat(changeRows);
        setSelectedRowKeys(selectedRowKeysHereSet);
        setSelectedRows(selectedRowsHereSet);
        setSelectedList(selectedRowsHereSet);
      } else {
        for (let z = 0; z < changeRows.length; z++) {
          selectedRowKeysHere.splice(
            selectedRowKeysHere?.indexOf(changeRows[z]?.id),
            1
          );
          const index = selectedRowsHere?.findIndex((obj: any) => {
            return obj?.id === changeRows[z]?.id;
          });
          selectedRowsHere.splice(index, 1);
        }
        setSelectedRowKeys(selectedRowKeysHere);
        setSelectedRows(selectedRowsHere);
        setSelectedList(selectedRowsHere);
      }
    },
    [selectedRowKeys, selectedRows]
  );

  const rowSelection = {
    onSelect(record: any, selected: boolean) {
      if (selected === true) {
        pushArrSelected(record);
      } else {
        deleteArrNotSelect(record);
      }
    },
    onSelectAll(selected: boolean, selectedRows: any, changeRows: any) {
      handleCheckAll(selected, selectedRows, changeRows);
    },
    selectedRowKeys,
    type: typeRowSelection,
    list: selectedRows,
  };

  const handleGetItemList = React.useCallback(
    async (filterValue) => {
      setLoading(true);
      const newFilter: AppUserFilter = {
        ...filterValue,

        statusId: {
          equal: 1,
        },
      };
      const getNCountItems = forkJoin([
        appUserGroupingRepository.listAppUser(newFilter),
        appUserGroupingRepository.countAppUser(newFilter),
      ])
        .pipe(
          finalize(() => {
            setLoading(false);
          })
        )
        .subscribe(
          (results: [AppUser[], number]) => {
            if (results[0]) {
              setList([...results[0]]);
              setTotal(Number(results[1]));
            }
          },
          (errors: any) => {}
        );
      subscription.add(getNCountItems);
    },
    [subscription]
  );

  const handleOpenModal = React.useCallback(
    () => (event: any) => {
      const list: any[] = [];
      if (listAppUser && listAppUser?.length > 0) {
        listAppUser.forEach((item: AppUser) => {
          list.push(item?.id);
        });
      }

      handleGetItemList({
        ...new AppUserFilter(),
        appUserGroupingId: { notIn: [model?.id] },
      });
      setAppUserFilter({
        ...new AppUserFilter(),
        appUserGroupingId: { notIn: [model?.id] },
      });

      setVisible(true);
    },
    [listAppUser, handleGetItemList, model]
  );
  const handleCloseModal = React.useCallback(() => {
    setVisible(false);
  }, []);

  const handleSave = React.useCallback(() => {
    if (selectedList && selectedList.length > 0) {
      const object = selectedRowKeys.map((id) => {
        return {
          appUserGroupingId: model?.id,
          appUserId: id,
          color: "#24a148",
        };
      });
      appUserGroupingRepository.bulkAddAppUser(object).subscribe((res) => {
        handleLoadList(filterAppUserTable);
        handleCloseModal();
        setSelectedRowKeys([]);
        setIsSave(true);
      });
    }
  }, [
    filterAppUserTable,
    handleCloseModal,
    handleLoadList,
    model?.id,
    selectedList,
    selectedRowKeys,
  ]);

  const handleFilter = React.useCallback(
    (fieldName: string, filterType: string, value?: string) => {
      const newFilter = { ...appUserFilter };
      newFilter[fieldName][filterType] = value;
      newFilter.skip = 0;
      setAppUserFilter({ ...newFilter });
      handleGetItemList(newFilter);
    },
    [appUserFilter, handleGetItemList]
  );

  const handleSelectFilter = React.useCallback(
    (fieldName: string, filterType: string) => (id?: number, value?: any) => {
      const newFilter = { ...appUserFilter };
      newFilter[`${fieldName}Id`][filterType] = id;
      newFilter[`${fieldName}Value`] = value;
      setAppUserFilter({ ...newFilter });
      handleGetItemList(newFilter);
    },
    [appUserFilter, handleGetItemList]
  );

  const handleChangePagination = React.useCallback(
    (skip: number, take: number) => {
      const filterValue = { ...appUserFilter };
      filterValue["skip"] = skip;
      filterValue["take"] = take;
      setAppUserFilter({ ...filterValue });

      handleGetItemList(filterValue);
    },
    [appUserFilter, handleGetItemList]
  );

  return {
    visible,
    list,
    total,
    appUserFilter,
    loading,
    handleOpenModal,
    handleSave,
    handleCloseModal,
    handleChangePagination,
    handleFilter,
    rowSelection,
    handleSelectFilter,
    isSave,
    setIsSave,
  };
}
interface AppUserGroupingAppUserMappingModalProps {
  visible: boolean;
  handleClose: () => void;
  handleSave: (data: AppUserGrouping) => void;
  translate: TFunction;
  appUserFilter?: AppUserFilter;
  handleChangeInputFilter?: TFunction;
  listAppUser?: AppUser[];
  countAppUser?: number;
  loadList?: boolean;
  handleTableChange?: (value: any) => void;
  handlePagination?: (skip: number, take: number) => void;
  rowSelection?: any;
  handleSelectFilter?: (
    fieldFilter: string,
    filterType: string
  ) => (value?: any) => void;
  isSave?: boolean;
}
export function AppUserGroupingAppUserMappingModal(
  props: AppUserGroupingAppUserMappingModalProps
) {
  const { validAction } = authorizationService.useAuthorizedAction(
    APP_USER_GROUPING_ROUTE.split("/")[
      APP_USER_GROUPING_ROUTE.split("/").length - 1
    ],
    API_APP_USER_GROUPING_PREFIX
  );

  const {
    visible,
    handleSave,
    handleClose,
    translate,
    appUserFilter,
    handleChangeInputFilter,
    listAppUser,
    countAppUser,
    loadList,
    handleTableChange,
    handlePagination,
    rowSelection,
  } = props;

  const debouncedFilterDisplayName = debounce((value: string) => {
    handleChangeInputFilter("displayName", "contain", value);
  }, 300);

  const debouncedFilterUserName = debounce((value: string) => {
    handleChangeInputFilter("username", "contain", value);
  }, 300);

  const columns: ColumnProps<AppUser>[] = useMemo(
    () => [
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUserGroupings.appUsers.organization")}
          />
        ),
        key: nameof(listAppUser[0].organization),
        dataIndex: nameof(listAppUser[0].organization),

        ellipsis: true,
        render(...params: [Organization, AppUser, number]) {
          return (
            <LayoutCell orderType="left" tableSize="sm">
              <OneLineText value={params[0]?.name} />
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUserGroupings.appUsers.username")}
          />
        ),
        key: nameof(listAppUser[0].username),
        dataIndex: nameof(listAppUser[0].username),

        ellipsis: true,
        render(...params: [string, AppUser, number]) {
          return (
            <LayoutCell orderType="left" tableSize="sm">
              <OneLineText value={params[0]} />
            </LayoutCell>
          );
        },
      },

      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUserGroupings.appUsers.displayName")}
          />
        ),
        key: nameof(listAppUser[0].displayName),
        dataIndex: nameof(listAppUser[0].displayName),

        ellipsis: true,
        render(...params: [string, AppUser, number]) {
          return (
            <LayoutCell orderType="left" tableSize="sm">
              <OneLineText value={params[0]} />
            </LayoutCell>
          );
        },
      },
    ],
    [translate, listAppUser]
  );

  return (
    <>
      <Modal
        size={720}
        visibleFooter={true}
        visible={visible}
        title={translate("appUserGroupings.appUsers.title")}
        handleSave={validAction("bulkAddAppUser") ? handleSave : undefined}
        handleCancel={handleClose}
        titleButtonCancel={translate("general.actions.close")}
        titleButtonApply={translate("general.actions.save")}
        closable={false}
        wrapClassName={"dxg-modal-wrap"}
      >
        <>
          <div className="modal-content">
            <Row>
              <Col lg={12} className="m-b--xxs p-r--sm">
                <AdvanceStringFilter
                  label={translate("appUserGroupings.appUsers.username")}
                  value={appUserFilter["username"]["contain"]}
                  onChange={debouncedFilterUserName}
                  placeHolder={translate(
                    "appUserGroupings.appUsers.placeholder.username"
                  )}
                  type={0}
                />
              </Col>
              <Col lg={12} className="m-b--xxs p-r--sm">
                <AdvanceStringFilter
                  label={translate("appUserGroupings.appUsers.displayName")}
                  value={appUserFilter["displayName"]["contain"]}
                  onChange={debouncedFilterDisplayName}
                  placeHolder={translate(
                    "appUserGroupings.appUsers.placeholder.displayName"
                  )}
                  type={0}
                />
              </Col>
            </Row>

            <div className=" m-t--sm">
              <StandardTable
                key={uuidv4()}
                rowKey={nameof(listAppUser[0].id)}
                columns={columns}
                dataSource={listAppUser}
                isDragable={true}
                tableSize={"md"}
                onChange={handleTableChange}
                loading={loadList}
                rowSelection={rowSelection}
              />
              <Pagination
                skip={appUserFilter?.skip}
                take={appUserFilter?.take}
                total={countAppUser}
                onChange={handlePagination}
                canChangePageSize={false}
              />
            </div>
          </div>

          <span className={"close-modal"} onClick={handleClose}>
            <Close20 />
          </span>
        </>
      </Modal>
    </>
  );
}
